import { useQuery } from "@tanstack/react-query";
import { useAuthReady } from "hooks/useAuthReady";
import { request } from "hooks/useOkoApi/useOkoApi";

// Each student's session collaboration score, as the backend computed it.
//
// Student Highlights and Student Detail label themselves with this rather
// than with the level the LLM narrated. A student with no scored question is absent
// from the response, and stays absent here — the report says nothing about them
// rather than implying a low score.

const EMPTY_ROLLUPS = {};

/**
 * @param {string|null|undefined} sessionId
 * @param {boolean} enabled Skip the request for sessions outside the gate.
 * @param {string|null} educatorId Scope the read to the educator who ran the
 *   session. The endpoint answers for its owner, so an admin reading someone
 *   else's session names them; teachers reading their own pass nothing.
 * @param {boolean} reconstruct Recompute the scores from the stored tagged
 *   transcript when the session carries none, for sessions that ran before the
 *   scorers shipped. The console asks for this because the prompt it is
 *   judging already assembles reconstructed scores; the teacher's report does
 *   not, so what a teacher reads is unchanged.
 * @returns {{byName: Object<string, number>, byId: Object<string, number>}}
 *   Levels keyed both ways; the report identifies students by name.
 */
export const useStudentCollabRollups = (
  sessionId,
  enabled = true,
  educatorId = null,
  reconstruct = false,
) => {
  const authReady = useAuthReady();

  const { data } = useQuery({
    queryKey: ["session-collab-rollups", sessionId, educatorId, reconstruct],
    queryFn: () => {
      // Asking for neither leaves the URL exactly as it was, which is what a
      // teacher's report requests.
      const query = new URLSearchParams({
        ...(educatorId ? { educator_id: educatorId } : {}),
        ...(reconstruct ? { reconstruct: "true" } : {}),
      }).toString();
      return request(
        `account/session_history_collab_rollups/${sessionId}${query ? `?${query}` : ""}`,
        { method: "GET" },
      );
    },
    enabled: Boolean(sessionId) && enabled && authReady,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const students = data?.students;
  if (!Array.isArray(students) || students.length === 0) {
    return { byName: EMPTY_ROLLUPS, byId: EMPTY_ROLLUPS };
  }

  const byName = {};
  const byId = {};
  students.forEach(({ student_id: id, student_name: name, student_collab_level: level }) => {
    if (id) byId[id] = level;
    if (name) byName[name.toLowerCase()] = level;
  });

  return { byName, byId };
};
